import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryNode, ViewQueryRecordNode } from '@salescore/core'

import { generateNewRecordId } from '../../state/nodeUtility'
import type { DeepReadonly } from '../types/DeepReadonly'

/**
 * parentRecordId を ID とするレコードの子レコードを新規作成する関数
 *
 * @param node - 新規作成するレコードのノード情報
 * @param parentRecordId - 親レコードのID。undefinedの場合、relationColumnが存在する場合は attributes は空、 changes.after に undefined キーで undefined が入る。
 *
 * @returns
 * newRecord: 生成した新規レコードノード
 * changes: 新規レコード作成に必要な ViewRecordFieldChange の配列
 *
 * 注意: newRecord が子レコードたちの配列の中でどこに入るのか、またどうやって入れるのかは呼び出し元の責務として残る
 */
export function generateNewChildRecord(
  node: DeepReadonly<Pick<ViewQueryNode, 'name' | 'read' | 'write'>>,
  parentRecordId: string | undefined,
  meta: ViewQueryRecordNode['meta'],
): {
  newRecord: ViewQueryRecordNode & { id: string }
  changes: ViewRecordFieldChange[]
} {
  const id = generateNewRecordId()
  const relationColumn =
    node.read.join?.joinOn.type === 'sql' ? undefined : node.read.join?.joinOn.meta.currentNodePropertyName
  const newRecord: ViewQueryRecordNode & { id: string } = {
    id,
    attributes: {
      ...(relationColumn !== undefined && parentRecordId !== undefined
        ? {
            // relationColumnは厳密にはattributesでないのでrecordNodeに含める必要はないが、changesのみに存在するのが分かりづらいためいれておく。
            // サーバーサイドのレスポンスも同様になっている。
            [`${node.name}_${relationColumn}`]: parentRecordId,
          }
        : {}),
    },
    children: [],
    meta,
  }
  const changes: ViewRecordFieldChange[] = [
    {
      id,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- 既存仕様を維持する。将来見直すべき。
      streamName: node.write!.streamName,
      fieldChanges: [],
      before: {},
      after: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- 上記と同様、既存仕様の維持
        [relationColumn!]: parentRecordId,
      },
    },
  ]

  return {
    newRecord,
    changes,
  }
}
